import React, { useState, useEffect, useRef } from "react";
import { cls } from "cls";
import camelcase from "camelcase";
import "../../Styles/RequiredMark.less";
import "../../Styles/FormCheckbox.less";

interface FormCheckboxProps {
  label: string;
  description: string;
  formTextColor: string;
  isRequired: boolean;
  errorMessage?: string;
  inputName: string;
  setFormValue: (key: string, value: string | number | boolean) => void;
}

const FormCheckbox = ({
  label,
  description,
  formTextColor,
  isRequired,
  errorMessage,
  inputName,
  setFormValue,
}: FormCheckboxProps): JSX.Element => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const handleOnChange = () => {
    setIsChecked(!isChecked);
    setFormValue(inputName, !isChecked);
  };

  const handleLinkClick = (e: Event) => {
    if (!e.target) {
      return;
    }

    if (!(e.target instanceof Element)) {
      return;
    }

    const origin = e.target.closest("a");

    if (!origin || !origin.href) {
      return;
    }

    if (origin && origin.href.toLowerCase().includes("webstaurantstore.com")) {
      e.preventDefault();
      window.open(origin.href, "_blank");
    }
  };

  useEffect(() => {
    const { current } = descriptionRef;

    if (descriptionRef && current) {
      current.addEventListener("click", (e: Event) => handleLinkClick(e));
    }

    return () => {
      if (descriptionRef && current) {
        current.removeEventListener("click", (e: Event) => handleLinkClick(e));
      }
    };
  }, [descriptionRef]);

  return (
    <div className={cls("flex", "flex-col", "items-start", "pb-4")}>
      <div className={cls("flex", "items-start", "space-x-2", "w-full")}>
        <input
          className={cls("!mt-0", {
            error: errorMessage,
            checkbox: !errorMessage,
          })}
          type="checkbox"
          name={camelcase(label)}
          id={`${label}-checkbox`}
          checked={isChecked}
          onChange={handleOnChange}
        />
        <label
          htmlFor={`${label}-checkbox`}
          className={cls("mb-1", "leading-4", {
            required: isRequired,
          })}
          style={{
            color: formTextColor,
            textShadow: "none",
          }}
        >
          {label}
        </label>
      </div>
      {!!description && (
        <div
          ref={descriptionRef}
          className={`description ${cls(
            "text-sm",
            "font-normal",
            "mb-0",
          )} ml-[22px]`}
          style={{ color: formTextColor }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      <span
        className={`${cls("h-4", "mb-0", "text-xs", "text-red-600")}`}
        style={{ visibility: errorMessage ? "visible" : "hidden" }}
      >
        Checkbox is required
      </span>
    </div>
  );
};

export default FormCheckbox;
